import logger from '../lib/logger';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { stateApi, projectsApi } from '../lib/api';
import { toast } from 'sonner';
import { MAX_PROJECTORS } from '../components/Editor/ProjectionSystem';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Aspect ratio presets
export const ASPECT_RATIOS = {
  '16:10': { width: 16, height: 10, value: 1.6 },
  '16:9': { width: 16, height: 9, value: 1.778 },
  '4:3': { width: 4, height: 3, value: 1.333 },
  '21:9': { width: 21, height: 9, value: 2.333 },
  '1:1': { width: 1, height: 1, value: 1 }
};

// Unit conversion factors to meters (internal unit is always meters)
export const UNITS = {
  'mm': { label: 'Millimeters', factor: 0.001, decimals: 0 },
  'cm': { label: 'Centimeters', factor: 0.01, decimals: 1 },
  'm': { label: 'Meters', factor: 1, decimals: 2 },
  'in': { label: 'Inches', factor: 0.0254, decimals: 2 },
  'ft': { label: 'Feet', factor: 0.3048, decimals: 2 }
};

// Convert from meters to display unit
export const metersToUnit = (meters, unit) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  return meters / unitConfig.factor;
};

// Convert from display unit to meters
export const unitToMeters = (value, unit) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  return value * unitConfig.factor;
};

// Format value for display with unit suffix
export const formatWithUnit = (meters, unit, showUnit = true) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  const value = meters / unitConfig.factor;
  const formatted = unitConfig.decimals === 0 ? Math.round(value).toString() : value.toFixed(unitConfig.decimals);
  return showUnit ? `${formatted} ${unit}` : formatted;
};

/**
 * Generate incremental name for objects
 * Format: PREFIX-001, PREFIX-002, etc.
 * @param {string} prefix - The prefix for the name (e.g., "PR", "Truss", "Hemisphere")
 * @param {Array} existingItems - Array of existing items with 'name' property
 * @returns {string} - New incremental name
 */
const generateIncrementalName = (prefix, existingItems) => {
  // Extract all numbers from existing names with the same prefix
  const existingNumbers = existingItems
    .map(item => {
      const match = item.name?.match(new RegExp(`^${prefix}-(\\d+)$`, 'i'));
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(num => num > 0);
  
  // Find the next available number
  let nextNum = 1;
  while (existingNumbers.includes(nextNum)) {
    nextNum++;
  }
  
  // Format with leading zeros (001, 002, etc.)
  return `${prefix}-${String(nextNum).padStart(3, '0')}`;
};

/**
 * Smart duplicate naming — picks up wherever the SOURCE object's current
 * name leaves off, even if the user manually renamed it.
 *
 *   "Floor Proj 001"   -> "Floor Proj 002"
 *   "Wall-007"         -> "Wall-008"
 *   "Mesh LED A-009"   -> "Mesh LED A-010"
 *   "Cube"             -> "Cube-001"   (no trailing number)
 *   "PR Front Wall 015"-> "PR Front Wall 016"
 *
 * Rules:
 *  - The base is everything before the trailing run of digits (any separator
 *    — space, dash, none — is preserved verbatim).
 *  - Pad-width matches the source's digit count, with a 3-digit minimum.
 *  - We scan ALL existing names that share the same base and pick
 *    `max + 1` so a manually-set higher number never gets overwritten.
 */
const generateDuplicateName = (sourceName, existingItems = []) => {
  const trimmed = (sourceName ?? '').toString();
  if (!trimmed) return generateIncrementalName('Object', existingItems);

  const match = trimmed.match(/^(.*?)(\d+)\s*$/);
  let base;
  let currentNum;
  let padWidth;
  if (match) {
    base = match[1];
    currentNum = parseInt(match[2], 10);
    padWidth = Math.max(3, match[2].length);
  } else {
    // No trailing digits — append "-NNN" so "Cube" becomes "Cube-001".
    // Don't double-up separators if the user already ended in '-' or ' '.
    base = /[-_ ]$/.test(trimmed) ? trimmed : `${trimmed}-`;
    currentNum = 0;
    padWidth = 3;
  }

  const escBase = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Match the same base followed by ANY length run of digits — so a 4-digit
  // sibling ("Wall-1000") still bumps a 3-digit family correctly.
  const familyRe = new RegExp(`^${escBase}(\\d+)\\s*$`, 'i');
  let maxNum = currentNum;
  for (const item of existingItems) {
    const nm = item?.name;
    if (!nm) continue;
    const mm = nm.match(familyRe);
    if (mm) {
      const n = parseInt(mm[1], 10);
      if (n > maxNum) maxNum = n;
    }
  }

  // Build the next candidate; bump past any manually-set duplicate.
  let next = maxNum + 1;
  // eslint-disable-next-line no-loop-func
  while (existingItems.some(it => it?.name === `${base}${String(next).padStart(padWidth, '0')}`)) {
    next += 1;
  }
  return `${base}${String(next).padStart(padWidth, '0')}`;
};

/**
 * Get the appropriate prefix for a scene object based on its type
 * @param {object} object - The scene object
 * @returns {string} - The prefix to use
 */
const getObjectPrefix = (object) => {
  const type = object.type?.toLowerCase() || '';
  const name = object.name?.toLowerCase() || '';
  
  // Check for specific object types - match actual type names used in the app
  if (type === 'led_screen' || name.includes('led')) {
    // Check if it's a soft corner or corner based on ledConfig
    const ledMode = object.ledConfig?.ledMode;
    if (ledMode === 'soft_corner') return 'LED Soft Corner';
    if (ledMode === 'corner') return 'LED Corner';
    return 'LED Screen';
  }
  if (type === 'scaffolding' || name.includes('scaffolding')) return 'Scaffolding';
  if (type === 'truss_system') return 'Truss';
  if (type === 'truss' || name.includes('truss')) return 'Truss';
  if (type === 'hemisphere' || name.includes('hemisphere')) return 'Hemisphere';
  if (type === 'dome' || name.includes('dome')) return 'Dome';
  if (type === 'sphere' || name.includes('sphere')) return 'Sphere';
  if (type === 'cube' || name.includes('cube')) return 'Cube';
  if (type === 'box' || name.includes('box')) return 'Box';
  // Order matters — specific screen types must be checked BEFORE the
  // generic 'screen' / 'plane' bucket below, otherwise their friendly
  // names get clobbered to "Plane".
  if (type === 'scrim_screen' || name.includes('scrim')) return 'Scrim Screen';
  if (name.includes('16:10')) return '16:10 Screen';
  if (type === 'screen' || name === 'screen') return 'Screen';
  if (type === 'plane' || type === 'projection_screen' || name.includes('plane') || name.includes('screen')) return 'Plane';
  if (type === 'cylinder' || name.includes('cylinder')) return 'Cylinder';
  if (type === 'pipe' || name.includes('pipe')) return 'Pipe';
  if (type === 'cone' || name.includes('cone')) return 'Cone';
  if (type === 'torus' || name.includes('torus')) return 'Torus';
  if (type === 'ground' || name.includes('ground') || name.includes('floor')) return 'Ground';
  if (type === 'mesh' || name.includes('mesh')) return 'Mesh';
  
  // Default: capitalize first letter of type or use "Object"
  if (type) {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }
  return 'Object';
};

// Max undo history size
const MAX_HISTORY = 50;

/**
 * Rehydrate a scene object with default values
 * This ensures all properties are properly initialized when loading
 * @param {Object} obj - The saved scene object data
 * @returns {Object} - Scene object with all required fields filled in
 */
const rehydrateSceneObject = (obj) => {
  // LED rotation migration: historically LED walls were spawned with rotation.y = 180°
  // to make the content face the default front view. New convention: rotation.y = 0 with
  // the display surface built on the viewer-facing side. Auto-migrate legacy values so
  // old projects render identically without the user needing to re-orient each wall.
  //
  // CRITICAL: This migration must only run ONCE per object. Without the version marker
  // it would re-zero any user-typed 180° rotation every time the object passes through
  // rehydrate (undo/redo, cloud round-trip, project reload) — turning typing "180" into
  // an invisible "snap to 0" bug. The marker is stamped on every rehydrated object, so
  // any object that has already gone through rehydrate at least once is left untouched.
  let migratedRotation = obj.rotation || { x: 0, y: 0, z: 0 };
  const alreadyMigrated = (obj.rotationMigrationVersion || 0) >= 1;
  if (!alreadyMigrated && obj.type === 'led_screen') {
    const ry = migratedRotation.y;
    if (ry !== undefined && ry !== null && Math.abs(Math.abs(ry) - 180) < 0.01) {
      migratedRotation = { ...migratedRotation, y: 0 };
    }
  }
  return {
    // Core identifiers
    id: obj.id,
    name: obj.name || 'Object',
    type: obj.type || 'primitive',
    originalType: obj.originalType || obj.type,
    
    // File reference for loaded meshes
    file_id: obj.file_id || null,
    primitive_type: obj.primitive_type || null,
    
    // Transform properties - CRITICAL for size restoration
    position: obj.position || { x: 0, y: 0, z: 0 },
    rotation: migratedRotation,
    rotationMigrationVersion: 1,
    scale: obj.scale || { x: 1, y: 1, z: 1 },
    
    // Dimensions - CRITICAL for primitive sizes
    dimensions: obj.dimensions || { width: 1, height: 1, depth: 1 },
    
    // Primitive-specific properties
    radius: obj.radius,
    diameter: obj.diameter,
    height: obj.height,
    segments: obj.segments,
    tessellation: obj.tessellation,
    sideOrientation: obj.sideOrientation,
    
    // Visibility & interaction
    visible: obj.visible ?? true,
    locked: obj.locked ?? false,
    
    // Appearance
    color: obj.color || '#888888',
    
    // Textures
    uvMap: obj.uvMap || null,
    uvMapUrl: obj.uvMapUrl || null,
    texture: obj.texture || null,
    textureUrl: obj.textureUrl || null,
    textureName: obj.textureName || null, // Name of the texture from Content library
    contentItemId: obj.contentItemId || null, // ID of the content item in Content library
    textureBrightness: obj.textureBrightness ?? 100, // Texture brightness percentage (0-200)
    uvMappingEnabled: obj.uvMappingEnabled ?? false, // UV Mapping toggle for reverse projection
    // Universal Texture Mapping System — 'auto' falls back to triplanar when
    // the mesh has no valid UVs. Existing saves default to 'auto' so nothing
    // visually changes for current users until they opt into another mode.
    texture_mapping_mode: obj.texture_mapping_mode || 'auto',
    texture_mapping_scale: obj.texture_mapping_scale ?? 1.0,
    
    // Mesh editing data
    editedMeshData: obj.editedMeshData || null,
    deletedComponents: obj.deletedComponents || [],
    hiddenComponents: obj.hiddenComponents || [],
    
    // Preserve any other properties
    ...obj,
    // LED rotation migration MUST come after ...obj so it wins against the legacy value
    rotation: migratedRotation
  };
};

/**
 * Rehydrate a projector with default values and spec lookup
 * This is a pure function that can be called during loading
 * @param {Object} projector - The saved projector data
 * @param {Object} library - The projector library — accepts `brands` (preferred,
 *   { Barco: [...], Panasonic: [...], ... }) and/or legacy `barco`/`panasonic`/`custom` arrays.
 * @returns {Object} - Projector with all required fields filled in
 */
// Legacy spec_id aliases mirror backend `LEGACY_SPEC_ID_ALIASES`. Saved scenes
// from prior releases used these ids; we transparently rewrite them when looking
// up the matching spec from the new Excel-driven library.
const LEGACY_SPEC_ID_ALIASES = {
  'barco-udx-4k40': 'barco-udx-4k40-flex',
  'panasonic-rz970': 'panasonic-pt-rz970',
};

const collectAllSpecs = (library = {}) => {
  // Prefer `library.brands` (dynamic, all manufacturers); fall back to legacy
  // hard-coded category arrays so older callers still work.
  const fromBrands = library.brands && typeof library.brands === 'object'
    ? Object.values(library.brands).flat()
    : [];
  const fromLegacy = [
    ...(library.barco || []),
    ...(library.panasonic || []),
  ];
  return [...fromBrands, ...fromLegacy, ...(library.custom || [])];
};

const rehydrateProjector = (projector, library = { brands: {}, custom: [] }) => {
  // Look up the spec from the projector library based on spec_id (resolving legacy aliases)
  let spec = projector.spec;
  let resolvedSpecId = projector.spec_id;
  if (resolvedSpecId && LEGACY_SPEC_ID_ALIASES[resolvedSpecId]) {
    resolvedSpecId = LEGACY_SPEC_ID_ALIASES[resolvedSpecId];
  }
  if (!spec && resolvedSpecId) {
    const allProjectors = collectAllSpecs(library);
    spec = allProjectors.find(p => p.id === resolvedSpecId)
      || allProjectors.find(p => p.id === projector.spec_id);
  }
  
  // Get default lens from spec
  const defaultLens = spec?.lenses?.[2] || spec?.lenses?.[0] || projector.selected_lens || null;
  
  // Return projector with all default values filled in
  return {
    // Core identifiers
    id: projector.id,
    spec_id: resolvedSpecId || projector.spec_id,
    name: projector.name || 'Projector',
    model: projector.model || (spec ? `${spec.brand} ${spec.model}` : 'Unknown'),
    
    // Position & rotation
    position: projector.position || { x: 0, y: 3, z: -5 },
    rotation: projector.rotation || { x: 0, y: 0, z: 0 },
    
    // Lens settings
    lens_shift_h: projector.lens_shift_h ?? 0,
    lens_shift_v: projector.lens_shift_v ?? 0,
    throw_ratio: projector.throw_ratio ?? (defaultLens ? (defaultLens.throw_ratio_min + defaultLens.throw_ratio_max) / 2 : 1.5),
    throw_distance: projector.throw_distance ?? 5,
    selected_lens_id: projector.selected_lens_id || defaultLens?.id || null,
    selected_lens: projector.selected_lens || defaultLens,
    custom_lenses: projector.custom_lenses || [],
    out_of_range: projector.out_of_range ?? false,
    
    // Display settings
    brightness: projector.brightness ?? 100,
    show_beam: projector.show_beam ?? true,
    beamOpacity: projector.beamOpacity ?? 0.2,
    show_grid: projector.show_grid ?? false,
    show_border: projector.show_border ?? true,
    show_frame: projector.show_frame ?? false,
    show_clamp: projector.show_clamp ?? false,
    color: projector.color || '#878787',
    body_color: projector.body_color || '#2a2a2a',
    
    // Body dimensions
    body_width: projector.body_width || spec?.width || 500,
    body_height: projector.body_height || spec?.height || 200,
    body_depth: projector.body_depth || spec?.depth || 600,
    weight: projector.weight || spec?.weight || 20,
    
    // Spec reference (critical for visualization)
    spec: spec || null,
    
    // Image/aspect settings
    aspect_ratio: projector.aspect_ratio || spec?.aspect_ratio || '16:10',
    image_width: projector.image_width || spec?.resolution_width || 1920,
    image_height: projector.image_height || spec?.resolution_height || 1200,
    lock_aspect_ratio: projector.lock_aspect_ratio ?? true,
    lock_image_size: projector.lock_image_size ?? false,
    projector_image: projector.projector_image || null,
    contentItemId: projector.contentItemId || null, // ID of the content item in Content library
    
    // Power and effects
    power_on: projector.power_on ?? true,  // CRITICAL: Default to ON
    shadow_enabled: projector.shadow_enabled ?? true,
    test_pattern_on: projector.test_pattern_on ?? false,
    test_pattern_id: projector.test_pattern_id || 'grid',
    lumens: projector.lumens || spec?.brightness || 10000,
    
    // Reverse projection mode - reveals object textures instead of projecting
    is_reverse_projector: projector.is_reverse_projector ?? false,
    
    // 90-degree lens
    lensType: projector.lensType || 'standard',
    lens90Rotation: projector.lens90Rotation || 'right',
    // Top/Bottom projection lens
    topBottomDirection: projector.topBottomDirection || 'top',
    
    // Name label visibility
    show_name: projector.show_name ?? true,
    
    // Lock state (persists across save/load)
    locked: projector.locked ?? false,
    width_locked: projector.width_locked ?? false,
    locked_width: projector.locked_width ?? null,
    
    // Frame & clamp weights
    frame_weight: projector.frame_weight || spec?.frame_weight || 0,
    clamp_weight: projector.clamp_weight ?? 2,

    // Wireframe / beam-edge outline toggle — defaults to ON when the
    // field is undefined so legacy projectors keep current behavior, but
    // an explicit `false` from the user persists correctly across refresh.
    show_beam_edges: projector.show_beam_edges ?? true,

    // Projector video source (optimized MP4 URL). Mutually exclusive with
    // `projector_image`; was silently stripped before because
    // rehydrateProjector only whitelists known keys.
    projector_video: projector.projector_video || null,
    projector_media_type: projector.projector_media_type || null,
    contentItemName: projector.contentItemName || null,

    // Virtual stacking config (top/bottom/left/right ghost projectors for
    // brightness boost). Was silently stripped before because the field
    // wasn't whitelisted, which is why stacking didn't survive a refresh.
    // `stack_color` is an optional ghost-only body color override.
    stacking: projector.stacking
      ? { ...projector.stacking }
      : { enabled: false, top: 0, bottom: 0, left: 0, right: 0, spacing: 0.5, stack_color: null },

    // Spread `...projector` LAST so any other custom UI-only fields the
    // user has set (textureScaleX/Y, textureOffsetX/Y, custom toggles,
    // imageSizeOverlayEnabled, lens90Rotation overrides, zoom, etc.)
    // survive a save → refresh → reopen cycle. Anything explicitly
    // listed above provides the default; this spread overrides with the
    // saved value when present. Mirror of rehydrateSceneObject behavior.
    ...projector,
    // Re-apply rotation/position/spec/stacking AFTER the spread so the
    // explicit logic above (legacy alias resolution, default lens, etc.)
    // wins over the raw saved values.
    spec_id: resolvedSpecId || projector.spec_id,
    spec: spec || null,
    selected_lens_id: projector.selected_lens_id || defaultLens?.id || null,
    selected_lens: projector.selected_lens || defaultLens,
    stacking: projector.stacking
      ? { ...projector.stacking }
      : { enabled: false, top: 0, bottom: 0, left: 0, right: 0, spacing: 0.5, stack_color: null },
  };
};

// Append scene-object rehydrator fields for the Universal Texture Mapping
// System. The scene-object rehydrator at line ~137 already spreads `...obj`
// last so these defaults are merged in automatically when the field is
// missing from the saved data — nothing else needed here.

export const AppProvider = ({ children, viewerMode = false }) => {
  const [isLoading, setIsLoading] = useState(!viewerMode);
  const autoSaveTimerRef = useRef(null);

  // Project settings
  const [projectSettings, setProjectSettings] = useState({ unit: 'm', name: 'Untitled Project' });

  // World/3D settings - 500m x 500m world by default
  const [worldSettings, setWorldSettings] = useState({
    backgroundColor: '#09090b',
    showGrid: true,
    gridSize: 500, // 500 meters total size
    gridColor: '#262626',
    gridLineColor: '#4d4d4d',
    gridStep: 1 // 1 meter (1000mm) grid lines by default
  });

  // Current project state
  const [currentProject, setCurrentProject] = useState(null);
  const [sceneObjects, setSceneObjects] = useState([]);
  const [projectors, setProjectors] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [multiSelectIds, setMultiSelectIds] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  
  // Measurements
  const [measurements, setMeasurements] = useState([]);
  const [measurementMode, setMeasurementMode] = useState(false); // true when measuring
  const [measurementStart, setMeasurementStart] = useState(null); // first point selected
  
  // Add Projector Dialog (shared between MenuBar and LibraryPanel)
  const [addProjectorDialogOpen, setAddProjectorDialogOpen] = useState(false);
  const [addProjectorSpec, setAddProjectorSpec] = useState(null);
  const [addWithFrame, setAddWithFrame] = useState(true);
  const [addWithClamp, setAddWithClamp] = useState(false);
  const [addAsReverseProjector, setAddAsReverseProjector] = useState(false);
  
  // Camera System
  const [savedCameras, setSavedCameras] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState(null);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [luxHeatmapEnabled, setLuxHeatmapEnabled] = useState(false);
  // Display mode: 'shaded' = normal materials/lighting, 'outline' = wireframe
  // (uses scene.forceWireframe — no mesh duplication, no material mutation)
  const [displayMode, setDisplayMode] = useState('shaded');
  
  // Undo/Redo history
  const [undoHistory, setUndoHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const isUndoingRef = useRef(false);
  const lastSavedStateRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  // Snapshot captured at the FIRST saveToHistory() call within a batch window.
  // Subsequent calls within the same batch must NOT overwrite this — that was
  // the original bug (snapshot kept moving forward with each rapid call so by
  // the time the debounce flushed, undo restored a half-applied state).
  const pendingSnapshotRef = useRef(null);
  
  // Refs that shadow current state — used by undo/redo to avoid stale closures
  const sceneObjectsRef = useRef(sceneObjects);
  const projectorsRef = useRef(projectors);
  const measurementsRef = useRef(measurements);
  const selectedObjectIdRef = useRef(selectedObjectId);
  sceneObjectsRef.current = sceneObjects;
  projectorsRef.current = projectors;
  measurementsRef.current = measurements;
  selectedObjectIdRef.current = selectedObjectId;
  
  // Transform mode
  const [transformMode, setTransformMode] = useState('none'); // 'none', 'position', 'rotation', 'scale'
  const [gizmoMode, setGizmoMode] = useState('translate'); // 'translate', 'rotate', 'scale'
  
  // View presets
  const [viewPreset, setViewPreset] = useState(null); // 'top', 'bottom', 'left', 'right', 'front', 'back', 'reset', or {type:'iso', corner:[x,y,z]}
  // Live main camera reference for the ViewCube overlay (set by BabylonScene on mount).
  // Holds a Babylon ArcRotateCamera so external UI (ViewCube) can read current alpha/beta/radius.
  const mainCameraRef = useRef(null);
  
  // Control mode - orbit or pan (hand tool)
  const [controlMode, setControlMode] = useState('orbit'); // 'orbit', 'pan'
  
  // Snap settings
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [snapDistance, setSnapDistance] = useState(0.5); // Snap distance in meters
  
  // Snap-to-ground callback ref — registered by BabylonScene, called by PropertiesPanel
  const snapToGroundFnRef = useRef(null);
  
  // Camera state ref — registered by BabylonScene, returns current camera params
  const getCameraStateRef = useRef(null);
  // Capture screenshot ref — registered by BabylonScene, returns data URL
  const captureScreenshotRef = useRef(null);
  
  // Scene panel state
  const [scenePanelOpen, setScenePanelOpen] = useState(true);
  const [scenePanelMinimized, setScenePanelMinimized] = useState(false);
  
  // Vertex Edit Mode state
  const [editMode, setEditMode] = useState(false);
  const [editingObjectId, setEditingObjectId] = useState(null);
  const [selectedVertices, setSelectedVertices] = useState([]);
  const [vertexGroups, setVertexGroups] = useState({}); // { groupName: [vertexIndices] }
  const [editModeHistory, setEditModeHistory] = useState([]);
  const [editModeRedoHistory, setEditModeRedoHistory] = useState([]);
  const [boxSelectStart, setBoxSelectStart] = useState(null);
  const [boxSelectEnd, setBoxSelectEnd] = useState(null);
  
  // UI state
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [activeLeftTab, setActiveLeftTab] = useState('projectors');
  const [activeRightTab, setActiveRightTab] = useState('properties');
  
  // Projector library
  const [projectorLibrary, setProjectorLibrary] = useState({ brands: {}, barco: [], panasonic: [], custom: [], test_patterns: [] });
  
  // Files
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Overlap analysis results
  const [overlapAnalysis, setOverlapAnalysis] = useState(null);

  // Get selected object
  const selectedObject = sceneObjects.find(obj => obj.id === selectedObjectId) || 
                         projectors.find(proj => proj.id === selectedObjectId);

  // Save state to undo history.
  // Pattern: capture the BEFORE-change snapshot on the FIRST call of a batch.
  // Subsequent calls within the debounce window only extend the timer — they
  // never overwrite the captured snapshot, so undo always restores the state
  // exactly as it was before the user started this action.
  // Uses refs (not state) so the callback is stable across renders and reads
  // fresh values regardless of when it's invoked.
  const captureCurrentSnapshot = useCallback(() => ({
    sceneObjects: JSON.parse(JSON.stringify(
      sceneObjectsRef.current.map(obj => ({ ...obj, modelData: undefined }))
    )),
    projectors: JSON.parse(JSON.stringify(projectorsRef.current)),
    measurements: JSON.parse(JSON.stringify(measurementsRef.current)),
    selectedObjectId: selectedObjectIdRef.current
  }), []);

  const pushSnapshotToHistory = useCallback((snapshot) => {
    if (!snapshot) return;
    const sig = JSON.stringify({
      sceneObjects: snapshot.sceneObjects,
      projectors: snapshot.projectors,
      measurements: snapshot.measurements
    });
    if (sig === lastSavedStateRef.current) return; // dedupe
    lastSavedStateRef.current = sig;
    setUndoHistory(prev => {
      const next = [...prev, snapshot];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setRedoHistory([]);
  }, [setUndoHistory, setRedoHistory]);

  const saveToHistory = useCallback(() => {
    if (isUndoingRef.current) return;

    // Capture BEFORE-state ONCE at the start of a batch
    if (!pendingSnapshotRef.current) {
      pendingSnapshotRef.current = captureCurrentSnapshot();
    }

    // Reset / extend the debounce timer; the captured snapshot stays put
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const snap = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      saveTimeoutRef.current = null;
      pushSnapshotToHistory(snap);
    }, 250);
  }, [captureCurrentSnapshot, pushSnapshotToHistory]);

  const saveToHistoryImmediate = useCallback(() => {
    if (isUndoingRef.current) return;

    // Flush any pending debounced snapshot first so we don't lose history
    // for the previous batch (which captured an earlier "before" state).
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      const pending = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      pushSnapshotToHistory(pending);
    }

    // Then capture and push the current "before" state for the immediate action
    pushSnapshotToHistory(captureCurrentSnapshot());
  }, [captureCurrentSnapshot, pushSnapshotToHistory]);

  // Undo action — uses refs to avoid stale closures from keyboard shortcuts
  const undo = useCallback(() => {
    setUndoHistory(prevUndo => {
      if (prevUndo.length === 0) {
        toast.info('Nothing to undo');
        return prevUndo;
      }

      isUndoingRef.current = true;

      // Cancel any in-flight batch save so it doesn't push a stale snapshot
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      pendingSnapshotRef.current = null;
      
      // Snapshot current state via refs (always fresh)
      const currentState = {
        sceneObjects: JSON.parse(JSON.stringify(sceneObjectsRef.current.map(obj => ({
          ...obj,
          modelData: undefined
        })))),
        projectors: JSON.parse(JSON.stringify(projectorsRef.current)),
        measurements: JSON.parse(JSON.stringify(measurementsRef.current)),
        selectedObjectId: selectedObjectIdRef.current
      };
      setRedoHistory(prev => [...prev, currentState]);
      
      // Restore previous state
      const previousState = prevUndo[prevUndo.length - 1];
      setSceneObjects(previousState.sceneObjects);
      setProjectors(previousState.projectors);
      if (previousState.measurements) setMeasurements(previousState.measurements);
      setSelectedObjectId(previousState.selectedObjectId);
      
      lastSavedStateRef.current = JSON.stringify({
        sceneObjects: previousState.sceneObjects.map(obj => ({ ...obj, modelData: undefined })),
        projectors: previousState.projectors,
        measurements: previousState.measurements || []
      });
      
      setTimeout(() => { isUndoingRef.current = false; }, 100);
      toast.success('Undo');
      
      return prevUndo.slice(0, -1);
    });
  }, [setSceneObjects, setProjectors, setMeasurements, setSelectedObjectId, setRedoHistory, setUndoHistory]);

  // Redo action — uses refs to avoid stale closures from keyboard shortcuts
  const redo = useCallback(() => {
    setRedoHistory(prevRedo => {
      if (prevRedo.length === 0) {
        toast.info('Nothing to redo');
        return prevRedo;
      }
      
      isUndoingRef.current = true;

      // Cancel any in-flight batch save so it doesn't push a stale snapshot
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      pendingSnapshotRef.current = null;
      
      // Snapshot current state via refs
      const currentState = {
        sceneObjects: JSON.parse(JSON.stringify(sceneObjectsRef.current.map(obj => ({
          ...obj,
          modelData: undefined
        })))),
        projectors: JSON.parse(JSON.stringify(projectorsRef.current)),
        measurements: JSON.parse(JSON.stringify(measurementsRef.current)),
        selectedObjectId: selectedObjectIdRef.current
      };
      setUndoHistory(prev => [...prev, currentState]);
      
      // Restore next state
      const nextState = prevRedo[prevRedo.length - 1];
      setSceneObjects(nextState.sceneObjects);
      setProjectors(nextState.projectors);
      if (nextState.measurements) setMeasurements(nextState.measurements);
      setSelectedObjectId(nextState.selectedObjectId);
      
      lastSavedStateRef.current = JSON.stringify({
        sceneObjects: nextState.sceneObjects.map(obj => ({ ...obj, modelData: undefined })),
        projectors: nextState.projectors,
        measurements: nextState.measurements || []
      });
      
      setTimeout(() => { isUndoingRef.current = false; }, 100);
      toast.success('Redo');
      
      return prevRedo.slice(0, -1);
    });
  }, [setSceneObjects, setProjectors, setMeasurements, setSelectedObjectId, setUndoHistory, setRedoHistory]);

  // Cloud save function (auto-save to app_state)
  const saveToCloud = useCallback(async () => {
    try {
      // Validate that we have valid data to save
      const cleanSceneObjects = (sceneObjects || []).map(obj => ({
        ...obj,
        modelData: undefined // Don't save Three.js objects to cloud
      }));
      
      const cleanProjectors = (projectors || []).map(proj => ({
        ...proj,
        // Remove any non-serializable data
        _customImageElement: undefined
      }));
      
      const state = {
        scene_objects: cleanSceneObjects,
        projectors: cleanProjectors,
        world_settings: worldSettings || {},
        project_settings: projectSettings || {},
        measurements: measurements || [],
        saved_cameras: savedCameras || [],
        // CRITICAL: Save current project ID so we can reload it on browser refresh
        current_project_id: currentProject?.id || null
      };
      
      await stateApi.save(state);
      
      // Also persist to the projects collection so data is available when
      // opening the project from the browser (not just on session reload)
      if (currentProject?.id && (cleanSceneObjects.length > 0 || cleanProjectors.length > 0)) {
        try {
          await projectsApi.update(currentProject.id, {
            scene_objects: cleanSceneObjects,
            projectors: cleanProjectors,
            unit: projectSettings?.unit,
            measurements: measurements || [],
            saved_cameras: savedCameras || [],
            world_settings: worldSettings || {}
          });
        } catch (projSaveErr) {
          // Non-critical — cloud state save succeeded, project save can retry next cycle
          logger.warn('Auto-save to projects collection failed:', projSaveErr);
        }
      }
      
      logger.log('Auto-saved to cloud (project ID:', currentProject?.id || 'none', ')');
      return { success: true };
    } catch (error) {
      logger.error('Cloud save error:', error);
      throw error;
    }
  }, [sceneObjects, projectors, worldSettings, projectSettings, currentProject, measurements, savedCameras]);
  
  // Save project to projects collection (manual save)
  const saveProject = useCallback(async () => {
    try {
      // Clean scene objects (remove non-serializable data)
      // Ensure all properties including rotation are preserved
      const cleanSceneObjects = (sceneObjects || []).map(obj => ({
        ...obj,
        modelData: undefined,
        // Explicitly include transform properties to ensure they're saved
        position: obj.position || { x: 0, y: 0, z: 0 },
        rotation: obj.rotation || { x: 0, y: 0, z: 0 },
        scale: obj.scale || { x: 1, y: 1, z: 1 },
        dimensions: obj.dimensions || { width: 1, height: 1, depth: 1 }
      }));
      
      // Clean projectors (remove non-serializable data)
      // Ensure all properties including test_pattern_on are preserved
      const cleanProjectors = (projectors || []).map(proj => ({
        ...proj,
        _customImageElement: undefined,
        // Explicitly include key projector properties
        test_pattern_on: proj.test_pattern_on ?? false,
        test_pattern_id: proj.test_pattern_id || 'grid',
        power_on: proj.power_on ?? true,
        shadow_enabled: proj.shadow_enabled ?? true
      }));
      
      if (currentProject?.id) {
        // Update existing project with ALL settings including world_settings
        await projectsApi.update(currentProject.id, {
          name: projectSettings.name || currentProject.name,
          scene_objects: cleanSceneObjects,
          projectors: cleanProjectors,
          unit: projectSettings.unit,
          measurements: measurements || [],
          saved_cameras: savedCameras || [],
          // Persist the full projectSettings so per-project overrides
          // (overlapBrightness, etc.) survive refresh / reopen.
          project_settings: projectSettings || {},
          world_settings: {
            backgroundColor: worldSettings.backgroundColor,
            showGrid: worldSettings.showGrid,
            gridSize: worldSettings.gridSize,
            gridColor: worldSettings.gridColor,
            gridLineColor: worldSettings.gridLineColor,
            gridStep: worldSettings.gridStep,
            testPatternLogo: worldSettings.testPatternLogo || null
          }
        });
        
        // Update current project reference
        setCurrentProject(prev => ({
          ...prev,
          scene_objects: cleanSceneObjects,
          projectors: cleanProjectors,
          measurements: measurements || [],
          name: projectSettings.name || prev?.name,
          world_settings: worldSettings
        }));
        
        // CRITICAL: Also save to cloud state immediately so browser reload works
        // This saves the current_project_id so the project is restored on reload
        const cloudState = {
          scene_objects: cleanSceneObjects,
          projectors: cleanProjectors,
          world_settings: worldSettings || {},
          project_settings: projectSettings || {},
          measurements: measurements || [],
          saved_cameras: savedCameras || [],
          current_project_id: currentProject.id
        };
        await stateApi.save(cloudState);
        logger.log('Project saved to both projects collection AND cloud state');
        
        setIsDirty(false);
        return { success: true };
      } else {
        // No project selected - just do auto-save
        await saveToCloud();
        setIsDirty(false);
        return { success: true };
      }
    } catch (error) {
      logger.error('Save project error:', error);
      throw error;
    }
  }, [sceneObjects, projectors, projectSettings, currentProject, worldSettings, measurements, savedCameras, saveToCloud]);

  // Save As — create a new project with the current scene data
  const saveProjectAs = useCallback(async (newName) => {
    try {
      const cleanSceneObjects = (sceneObjects || []).map(obj => ({
        ...obj,
        modelData: undefined,
        position: obj.position || { x: 0, y: 0, z: 0 },
        rotation: obj.rotation || { x: 0, y: 0, z: 0 },
        scale: obj.scale || { x: 1, y: 1, z: 1 },
        dimensions: obj.dimensions || { width: 1, height: 1, depth: 1 }
      }));
      const cleanProjectors = (projectors || []).map(proj => ({
        ...proj,
        _customImageElement: undefined,
        test_pattern_on: proj.test_pattern_on ?? false,
        test_pattern_id: proj.test_pattern_id || 'grid',
        power_on: proj.power_on ?? true,
        shadow_enabled: proj.shadow_enabled ?? true
      }));

      // Create new project
      const createRes = await projectsApi.create({ name: newName, unit: projectSettings?.unit || 'mm' });
      const newProject = createRes.data;

      // Save scene data to the new project
      await projectsApi.update(newProject.id, {
        scene_objects: cleanSceneObjects,
        projectors: cleanProjectors,
        unit: projectSettings?.unit,
        measurements: measurements || [],
        world_settings: {
          backgroundColor: worldSettings.backgroundColor,
          showGrid: worldSettings.showGrid,
          gridSize: worldSettings.gridSize,
          gridColor: worldSettings.gridColor,
          gridLineColor: worldSettings.gridLineColor,
          gridStep: worldSettings.gridStep,
          testPatternLogo: worldSettings.testPatternLogo || null
        }
      });

      // Switch to the new project
      const fullProject = { ...newProject, scene_objects: cleanSceneObjects, projectors: cleanProjectors };
      setCurrentProject(fullProject);
      setProjectSettings(prev => ({ ...prev, name: newName }));

      // Update cloud state with new project ID
      await stateApi.save({
        scene_objects: cleanSceneObjects,
        projectors: cleanProjectors,
        world_settings: worldSettings || {},
        project_settings: { ...projectSettings, name: newName },
        measurements: measurements || [],
        current_project_id: newProject.id
      });

      setIsDirty(false);
      return { success: true, project: fullProject };
    } catch (error) {
      logger.error('Save As error:', error);
      throw error;
    }
  }, [sceneObjects, projectors, projectSettings, currentProject, worldSettings, measurements, savedCameras]);

  // Load from cloud on startup (skip in viewer mode)
  useEffect(() => {
    if (viewerMode) return;
    const loadFromCloud = async () => {
      try {
        const response = await stateApi.load();
        if (response.data?.success && response.data?.state) {
          const state = response.data.state;
          
          // CRITICAL FIX: If a project was being worked on, load it from projects collection
          // This ensures we get the SAVED data, not just auto-save data
          if (state.current_project_id) {
            logger.log('Found saved project ID:', state.current_project_id, '- loading from projects collection');
            try {
              const projectResponse = await projectsApi.get(state.current_project_id);
              if (projectResponse.data) {
                const project = projectResponse.data;
                logger.log('Loading project:', project.name, 'with', project.scene_objects?.length, 'objects');
                
                // Set current project first
                setCurrentProject(project);
                
                // PREFER auto-save scene objects/projectors over project data
                // Auto-save is always more recent (fires on every state change)
                // This ensures changes like contentUrl from "Apply Pattern" persist
                const sceneSource = (state.scene_objects?.length > 0) ? state.scene_objects : (project.scene_objects || []);
                const projSource = (state.projectors?.length > 0) ? state.projectors : (project.projectors || []);
                
                const rehydratedSceneObjects = sceneSource.map(rehydrateSceneObject);
                setSceneObjects(rehydratedSceneObjects);
                
                const rehydratedProjectors = projSource.map(p => rehydrateProjector(p, {}));
                setProjectors(rehydratedProjectors);
                
                // Restore world settings — prefer auto-save (has testPatternLogo etc.)
                const wsSource = state.world_settings || project.world_settings || {};
                setWorldSettings({
                  backgroundColor: wsSource.backgroundColor || '#09090b',
                  showGrid: wsSource.showGrid ?? true,
                  gridSize: wsSource.gridSize || 500,
                  gridColor: wsSource.gridColor || '#262626',
                  gridLineColor: wsSource.gridLineColor || '#4d4d4d',
                  gridStep: wsSource.gridStep || 1, // 1 m default
                  testPatternLogo: wsSource.testPatternLogo || null
                });
                
                // Restore project settings — merge persisted project_settings
                // (overlapBrightness, etc.) with unit/name. Prefer autosave's
                // project_settings if present, otherwise fall back to the
                // project document's persisted copy.
                const psSource = state.project_settings || project.project_settings || {};
                setProjectSettings({
                  ...psSource,
                  unit: project.unit || psSource.unit || 'm',
                  name: project.name || psSource.name
                });
                
                // Restore measurements - prefer cloud state (auto-saved) over project collection
                const measurementsToRestore = (state.measurements?.length > 0) ? state.measurements : project.measurements;
                if (measurementsToRestore?.length > 0) {
                  setMeasurements(measurementsToRestore);
                }
                // Restore cameras
                const camerasToRestore = (state.saved_cameras?.length > 0) ? state.saved_cameras : (project.saved_cameras || []);
                setSavedCameras(camerasToRestore);
                
                toast.success(`Project "${project.name}" restored`);
                setIsLoading(false);
                return; // Don't load from cloud state
              }
            } catch (projectError) {
              logger.log('Could not load project, falling back to cloud state:', projectError);
            }
          }
          
          // Fallback: Load from cloud state (no project was active)
          if (state.scene_objects?.length > 0) {
            // Rehydrate scene objects with default values
            const rehydratedSceneObjects = state.scene_objects.map(rehydrateSceneObject);
            setSceneObjects(rehydratedSceneObjects);
          }
          if (state.projectors?.length > 0) {
            // Rehydrate projectors with default values (empty library on initial load is OK)
            const rehydratedProjectors = state.projectors.map(p => rehydrateProjector(p, {}));
            setProjectors(rehydratedProjectors);
          }
          if (state.world_settings) setWorldSettings(state.world_settings);
          if (state.project_settings) setProjectSettings(state.project_settings);
          if (state.measurements?.length > 0) setMeasurements(state.measurements);
          toast.success('Session restored from cloud');
        }
      } catch (error) {
        logger.log('No saved state found or error loading');
      } finally {
        setIsLoading(false);
      }
    };
    loadFromCloud();
  }, []);

  // Auto-save to cloud when state changes (debounced, skip in viewer mode)
  useEffect(() => {
    if (viewerMode) return;
    if (isLoading) return;
    if (editMode) return; // Don't auto-save while in mesh edit mode
    if (sceneObjects.length === 0 && projectors.length === 0 && measurements.length === 0) return;
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        await saveToCloud();
      } catch (error) {
        // Silent fail for auto-save - don't interrupt user
        logger.warn('Auto-save failed:', error);
      }
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [sceneObjects, projectors, worldSettings, projectSettings, isLoading, saveToCloud, editMode, measurements, savedCameras]);

  // ===== Camera System CRUD =====
  const createCamera = useCallback((cameraState) => {
    const id = crypto.randomUUID();
    const camCount = savedCameras.length + 1;
    const newCam = {
      id,
      name: `Camera ${camCount}`,
      position: cameraState.position,
      target: cameraState.target,
      alpha: cameraState.alpha,
      beta: cameraState.beta,
      radius: cameraState.radius,
      fov: cameraState.fov || 0.8,
      focalLength: cameraState.focalLength || 50,
      dofEnabled: false,
      aperture: 2.8,
      focusDistance: 10,
      aspectRatio: '16:9',
      created_at: new Date().toISOString()
    };
    setSavedCameras(prev => [...prev, newCam]);
    setSelectedCameraId(id);
    setIsDirty(true);
    return newCam;
  }, [savedCameras]);

  const updateCamera = useCallback((id, updates) => {
    setSavedCameras(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    setIsDirty(true);
  }, []);

  const deleteCamera = useCallback((id) => {
    setSavedCameras(prev => prev.filter(c => c.id !== id));
    if (activeCameraId === id) setActiveCameraId(null);
    if (selectedCameraId === id) setSelectedCameraId(null);
    setIsDirty(true);
  }, [activeCameraId, selectedCameraId]);

  const duplicateCamera = useCallback((id) => {
    const cam = savedCameras.find(c => c.id === id);
    if (!cam) return;
    const newId = crypto.randomUUID();
    const dup = { ...cam, id: newId, name: cam.name + ' (Copy)', created_at: new Date().toISOString() };
    setSavedCameras(prev => [...prev, dup]);
    setSelectedCameraId(newId);
    setIsDirty(true);
    return dup;
  }, [savedCameras]);



  // Convert value to current unit
  const toCurrentUnit = useCallback((meters) => {
    return meters / UNITS[projectSettings.unit].factor;
  }, [projectSettings.unit]);

  // Convert from current unit to meters
  const toMeters = useCallback((value) => {
    return value * UNITS[projectSettings.unit].factor;
  }, [projectSettings.unit]);

  // Add scene object with unit support
  const addSceneObject = useCallback((object) => {
    saveToHistoryImmediate();
    
    // For imported meshes, use the filename as the name
    // For other objects, generate incremental name based on type
    let objectName;
    if (object.type === 'mesh' && object.name) {
      // Use the provided filename for imported meshes
      // Remove file extension if present
      const baseName = object.name.replace(/\.[^/.]+$/, '');
      objectName = baseName;
    } else {
      // Generate incremental name based on object type
      const prefix = getObjectPrefix(object);
      objectName = generateIncrementalName(prefix, sceneObjects);
    }
    
    const newObject = {
      id: uuidv4(),
      ...object,
      name: objectName, // Use the determined name
      originalType: object.name || object.type, // Store original type info
      position: object.position || { x: 0, y: 0, z: 0 },
      rotation: object.rotation || { x: 0, y: 0, z: 0 },
      scale: object.scale || { x: 1, y: 1, z: 1 },
      dimensions: object.dimensions || { width: 1, height: 1, depth: 1 },
      visible: true,
      locked: false,
      uvMap: null,
      uvMapUrl: null,
      texture: null,
      textureUrl: null
    };
    setSceneObjects(prev => [...prev, newObject]);
    setSelectedObjectId(newObject.id);
    setIsDirty(true);
    toast.success(`Added ${objectName}`);
    return newObject;
  }, [saveToHistoryImmediate, sceneObjects]);

  // Update scene object
  const updateSceneObject = useCallback((id, updates) => {
    // Save to history before making changes (debounced for drag operations)
    saveToHistory();
    
    setSceneObjects(prev => prev.map(obj => 
      obj.id === id ? { ...obj, ...updates } : obj
    ));
    setIsDirty(true);
  }, [saveToHistory]);

  // Remove scene object
  const removeSceneObject = useCallback((id) => {
    saveToHistoryImmediate();
    setSceneObjects(prev => prev.filter(obj => obj.id !== id));
    if (selectedObjectId === id) {
      setSelectedObjectId(null);
    }
    setIsDirty(true);
    toast.success('Object deleted');
  }, [selectedObjectId, saveToHistoryImmediate]);

  // Duplicate selected object
  const duplicateSelected = useCallback(() => {
    if (!selectedObject) {
      toast.info('Select an object to duplicate');
      return;
    }

    saveToHistoryImmediate();
    const newId = uuidv4();

    if (projectors.find(p => p.id === selectedObjectId)) {
      const proj = projectors.find(p => p.id === selectedObjectId);
      // Continue the source's own naming sequence — user may have manually
      // renamed "PR-001" to "Stage Right 005", so we want "Stage Right 006"
      // instead of jumping back to the type prefix "PR-002".
      setProjectors(prev => {
        const newName = generateDuplicateName(proj.name, prev);
        const newProj = {
          ...JSON.parse(JSON.stringify(proj)),
          id: newId,
          name: newName
        };
        toast.success(`Duplicated as ${newName}`);
        return [...prev, newProj];
      });
      setSelectedObjectId(newId);
    } else {
      const obj = sceneObjects.find(o => o.id === selectedObjectId);
      setSceneObjects(prev => {
        const newName = generateDuplicateName(obj.name, prev);
        const newObj = {
          ...JSON.parse(JSON.stringify(obj)),
          id: newId,
          name: newName,
          modelData: undefined
        };
        toast.success(`Duplicated as ${newName}`);
        return [...prev, newObj];
      });
      setSelectedObjectId(newId);
    }

    setIsDirty(true);
  }, [selectedObject, selectedObjectId, projectors, sceneObjects, saveToHistoryImmediate]);

  // Duplicate all items in multiSelectIds - each selected item duplicates once
  const duplicateMultiSelected = useCallback((idsToClone) => {
    if (!idsToClone || idsToClone.length === 0) {
      toast.info('Select objects to duplicate');
      return;
    }

    saveToHistoryImmediate();

    const newProjectors = [];
    const newObjects = [];

    // Duplicate projectors — name continues each source's own sequence.
    setProjectors(prev => {
      let updatedList = [...prev];
      idsToClone.forEach(id => {
        const proj = prev.find(p => p.id === id);
        if (proj) {
          const newName = generateDuplicateName(proj.name, updatedList);
          const newProj = {
            ...JSON.parse(JSON.stringify(proj)),
            id: uuidv4(),
            name: newName
          };
          updatedList = [...updatedList, newProj];
          newProjectors.push(newProj);
        }
      });
      return updatedList;
    });

    // Duplicate scene objects — same rule: respect each source's own name.
    setSceneObjects(prev => {
      let updatedList = [...prev];
      idsToClone.forEach(id => {
        const obj = prev.find(o => o.id === id);
        if (obj) {
          const newName = generateDuplicateName(obj.name, updatedList);
          const newObj = {
            ...JSON.parse(JSON.stringify(obj)),
            id: uuidv4(),
            name: newName,
            modelData: undefined
          };
          updatedList = [...updatedList, newObj];
          newObjects.push(newObj);
        }
      });
      return updatedList;
    });

    const totalDuplicated = newProjectors.length + newObjects.length;
    if (totalDuplicated > 0) {
      toast.success(`Duplicated ${totalDuplicated} items`);
    }

    setIsDirty(true);
  }, [saveToHistoryImmediate]);

  // Delete selected object
  const deleteSelected = useCallback(() => {
    if (!selectedObjectId) {
      toast.info('Select an object to delete');
      return;
    }
    
    saveToHistoryImmediate();
    
    if (projectors.find(p => p.id === selectedObjectId)) {
      setProjectors(prev => prev.filter(p => p.id !== selectedObjectId));
      toast.success('Projector deleted');
    } else {
      setSceneObjects(prev => prev.filter(o => o.id !== selectedObjectId));
      toast.success('Object deleted');
    }
    
    setSelectedObjectId(null);
    setIsDirty(true);
  }, [selectedObjectId, projectors, saveToHistoryImmediate]);

  // Select all objects
  const selectAll = useCallback(() => {
    if (sceneObjects.length > 0) {
      setSelectedObjectId(sceneObjects[0].id);
      toast.info('Use Shift+Click to multi-select');
    }
  }, [sceneObjects]);

  // Deselect all
  const deselectAll = useCallback(() => {
    setSelectedObjectId(null);
  }, []);

  // Show/Hide all beams
  const showAllBeams = useCallback(() => {
    setProjectors(prev => prev.map(p => ({ ...p, show_beam: true })));
    toast.success('All beams visible');
  }, []);

  const hideAllBeams = useCallback(() => {
    setProjectors(prev => prev.map(p => ({ ...p, show_beam: false })));
    toast.success('All beams hidden');
  }, []);

  // Toggle grid
  const toggleGrid = useCallback(() => {
    setWorldSettings(prev => ({ ...prev, showGrid: !prev.showGrid }));
  }, []);

  // Add projector with enhanced settings
  // isReverseProjector: when true, creates a reverse projector that reveals object textures
  const addProjector = useCallback((spec, isReverseProjector = false) => {
    // GPU memory check - warn if approaching limit
    const currentCount = projectors.length;
    if (currentCount >= MAX_PROJECTORS) {
      toast.error(`Cannot add projector: Maximum limit (${MAX_PROJECTORS}) reached. Remove existing projectors first.`);
      return null;
    }
    if (currentCount >= 24) {
      toast.warning(`Performance notice: ${currentCount + 1} projectors active. Performance may degrade on some hardware.`);
    }

    saveToHistoryImmediate();
    const defaultLens = spec.lenses?.[2] || spec.lenses?.[0] || null;
    const newId = uuidv4();
    
    // Use functional update to get LATEST projectors list for name generation
    // This prevents duplicate names when adding projectors rapidly
    setProjectors(prev => {
      // Use different prefix for reverse projectors (RPR = Reverse PRojector)
      const namePrefix = isReverseProjector ? 'RPR' : 'PR';
      const projectorName = generateIncrementalName(namePrefix, prev);
      const newProjector = {
        id: newId,
        spec_id: spec.id,
        name: projectorName,
        model: `${spec.brand} ${spec.model}`,
        is_reverse_projector: isReverseProjector, // Flag for reverse projection mode
        position: { x: 0, y: 3, z: -5 },
        rotation: { x: 0, y: 0, z: 0 },
        lensType: 'standard', // 'standard' | 'lens90' | 'topBottom'
        lens90Rotation: 'right', // 'left', 'right', 'top', 'bottom'
        topBottomDirection: 'top', // 'top' (projects down) | 'bottom' (projects up)
        lens_shift_h: 0,
        lens_shift_v: 0,
        brightness: 100,
        throw_ratio: defaultLens ? (defaultLens.throw_ratio_min + defaultLens.throw_ratio_max) / 2 : 1.5,
        show_beam: true,
        beamOpacity: 0.2,
        show_grid: false,
        show_border: true, // Border toggle works for both normal and reverse projectors
        show_frame: spec.showFrame || false,
        show_clamp: spec.showClamp || false,
        color: '#878787',
        body_color: '#2a2a2a',
        body_width: spec.width || 500,
        body_height: spec.height || 200,
        body_depth: spec.depth || 600,
        weight: spec.weight || 20,
        spec: spec,
        aspect_ratio: spec.aspect_ratio || '16:10',
        image_width: spec.resolution_width || 1920,
        image_height: spec.resolution_height || 1200,
        lock_aspect_ratio: true,
        lock_image_size: false,
        projector_image: null,
        throw_distance: 5,
        power_on: true,
        shadow_enabled: true,
        test_pattern_on: false,
        test_pattern_id: 'grid',
        selected_lens_id: defaultLens?.id || null,
        selected_lens: defaultLens,
        custom_lenses: [],
        out_of_range: false,
        lumens: spec.brightness || 10000
      };
      const projectorType = isReverseProjector ? 'Reverse Projector' : 'Projector';
      toast.success(`Added ${projectorName} - ${projectorType} (${spec.brand} ${spec.model})`);
      return [...prev, newProjector];
    });
    setSelectedObjectId(newId);
    setIsDirty(true);
    return { id: newId };
  }, [saveToHistoryImmediate, projectors]);

  // Update projector with validation
  const updateProjector = useCallback((id, updates) => {
    // Save to history before making changes (debounced for drag operations)
    saveToHistory();
    
    setProjectors(prev => prev.map(proj => {
      if (proj.id !== id) return proj;
      
      const updated = { ...proj, ...updates };
      
      if (updates.selected_lens_id) {
        const lens = [...(proj.spec?.lenses || []), ...(proj.custom_lenses || [])]
          .find(l => l.id === updates.selected_lens_id);
        if (lens) {
          updated.selected_lens = lens;
          updated.throw_ratio = (lens.throw_ratio_min + lens.throw_ratio_max) / 2;
          // Reset lens shift to center when switching lens
          if (lens.lens_shift_h !== undefined) {
            updated.lens_shift_h = 0;
          }
          if (lens.lens_shift_v !== undefined) {
            updated.lens_shift_v = 0;
          }
        }
      }
      
      if (updated.selected_lens) {
        const { throw_ratio_min, throw_ratio_max } = updated.selected_lens;
        updated.out_of_range = updated.throw_ratio < throw_ratio_min || updated.throw_ratio > throw_ratio_max;
      }
      
      if (updates.aspect_ratio && proj.lock_aspect_ratio) {
        const ratio = ASPECT_RATIOS[updates.aspect_ratio];
        if (ratio) {
          updated.image_width = Math.round(updated.image_height * ratio.value);
        }
      }
      
      if (updates.image_width && proj.lock_aspect_ratio) {
        const ratio = ASPECT_RATIOS[proj.aspect_ratio];
        if (ratio) {
          updated.image_height = Math.round(updates.image_width / ratio.value);
        }
      }
      if (updates.image_height && proj.lock_aspect_ratio) {
        const ratio = ASPECT_RATIOS[proj.aspect_ratio];
        if (ratio) {
          updated.image_width = Math.round(updates.image_height * ratio.value);
        }
      }
      
      // When throw_distance changes, move the projector along its projection axis
      // This keeps the projector at the correct distance from the target
      if (updates.throw_distance !== undefined && updates.throw_distance !== proj.throw_distance) {
        const oldDistance = proj.throw_distance || 5;
        const newDistance = updates.throw_distance;
        const distanceDelta = newDistance - oldDistance;
        
        // Calculate projection direction from rotation
        const rotX = proj.rotation?.x || 0;
        const rotY = proj.rotation?.y || 0;
        
        // Direction vector (projector points along Z axis, then rotated)
        // Default forward direction is (0, 0, 1)
        const cosX = Math.cos(rotX);
        const sinX = Math.sin(rotX);
        const cosY = Math.cos(rotY);
        const sinY = Math.sin(rotY);
        
        // Calculate direction after rotation (Y rotation then X rotation)
        const dirX = sinY * cosX;
        const dirY = -sinX;
        const dirZ = cosY * cosX;
        
        // Move projector backward along its projection direction
        // (increasing throw distance means moving projector further from target)
        updated.position = {
          x: (proj.position?.x || 0) - dirX * distanceDelta,
          y: (proj.position?.y || 0) - dirY * distanceDelta,
          z: (proj.position?.z || 0) - dirZ * distanceDelta
        };
      }
      
      return updated;
    }));
    setIsDirty(true);
  }, []);

  // Add custom lens to projector
  const addCustomLens = useCallback((projectorId, lens) => {
    setProjectors(prev => prev.map(proj => {
      if (proj.id !== projectorId) return proj;
      return {
        ...proj,
        custom_lenses: [...(proj.custom_lenses || []), { ...lens, id: uuidv4() }]
      };
    }));
    setIsDirty(true);
  }, []);

  // Remove projector
  const removeProjector = useCallback((id) => {
    saveToHistoryImmediate();
    setProjectors(prev => prev.filter(proj => proj.id !== id));
    if (selectedObjectId === id) {
      setSelectedObjectId(null);
    }
    setIsDirty(true);
    toast.success('Projector deleted');
  }, [selectedObjectId, saveToHistoryImmediate]);

  // Measurement functions
  const addMeasurement = useCallback((measurement) => {
    saveToHistoryImmediate();
    const newMeasurement = {
      id: uuidv4(),
      name: measurement.name || `Measurement ${measurements.length + 1}`,
      type: measurement.type || 'point-to-point',
      startPoint: measurement.startPoint,
      endPoint: measurement.endPoint,
      startObjectId: measurement.startObjectId,
      endObjectId: measurement.endObjectId,
      distance: measurement.distance || 0,
      visible: true,
      lineColor: measurement.lineColor || '#00ff00',
      textColor: measurement.textColor || '#ffffff',
      textSize: measurement.textSize || 14
    };
    setMeasurements(prev => [...prev, newMeasurement]);
    setIsDirty(true);
    toast.success(`Added ${newMeasurement.name}`);
    return newMeasurement;
  }, [measurements.length, saveToHistoryImmediate]);

  const updateMeasurement = useCallback((id, updates) => {
    setMeasurements(prev => prev.map(m => 
      m.id === id ? { ...m, ...updates } : m
    ));
    setIsDirty(true);
  }, []);

  const removeMeasurement = useCallback((id) => {
    saveToHistoryImmediate();
    setMeasurements(prev => prev.filter(m => m.id !== id));
    setIsDirty(true);
    toast.success('Measurement deleted');
  }, [saveToHistoryImmediate]);

  const toggleMeasurementVisibility = useCallback((id) => {
    setMeasurements(prev => prev.map(m => 
      m.id === id ? { ...m, visible: !m.visible } : m
    ));
  }, []);

  const clearAllMeasurements = useCallback(() => {
    saveToHistoryImmediate();
    setMeasurements([]);
    setIsDirty(true);
    toast.success('All measurements cleared');
  }, [saveToHistoryImmediate]);

  const startMeasurement = useCallback((point, objectId = null) => {
    setMeasurementStart({ point, objectId });
    toast.info('Click second point to complete measurement');
  }, []);

  const completeMeasurement = useCallback((endPoint, endObjectId = null) => {
    if (!measurementStart) return;
    
    const startPoint = measurementStart.point;
    const distance = Math.sqrt(
      Math.pow(endPoint.x - startPoint.x, 2) +
      Math.pow(endPoint.y - startPoint.y, 2) +
      Math.pow(endPoint.z - startPoint.z, 2)
    );
    
    addMeasurement({
      type: 'point-to-point',
      startPoint,
      endPoint,
      startObjectId: measurementStart.objectId,
      endObjectId,
      distance
    });
    
    setMeasurementStart(null);
    setMeasurementMode(false);
  }, [measurementStart, addMeasurement]);

  const cancelMeasurement = useCallback(() => {
    setMeasurementStart(null);
    setMeasurementMode(false);
    toast.info('Measurement cancelled');
  }, []);

  // ============ VERTEX EDIT MODE FUNCTIONS ============
  
  // Enter edit mode for an object
  const enterEditMode = useCallback((objectId) => {
    const obj = sceneObjects.find(o => o.id === objectId);
    if (!obj) {
      toast.error('Object not found');
      return;
    }
    setEditMode(true);
    setEditingObjectId(objectId);
    setSelectedVertices([]);
    setVertexGroups({});
    setEditModeHistory([]);
    setEditModeRedoHistory([]);
    toast.success(`Edit mode: ${obj.name}`);
  }, [sceneObjects]);

  // Exit edit mode
  const exitEditMode = useCallback((saveChanges = true) => {
    if (saveChanges && editingObjectId) {
      // Get component state (deleted/hidden components) - ALWAYS check this
      let componentState = { deletedComponents: [], hiddenComponents: [] };
      if (window.getComponentState) {
        componentState = window.getComponentState();
        logger.log('Component state to save:', componentState);
      }
      
      // Get the edited mesh data from Babylon (for vertex edits)
      let editedMeshData = null;
      if (window.getEditModeVertexData) {
        const meshData = window.getEditModeVertexData();
        if (meshData && meshData.mesh) {
          try {
            const positions = meshData.mesh.getVerticesData('position');
            const indices = meshData.mesh.getIndices();
            const normals = meshData.mesh.getVerticesData('normal');
            
            if (positions && indices) {
              logger.log('Saving edited mesh data:', positions.length, 'positions,', indices.length, 'indices');
              editedMeshData = {
                positions: Array.from(positions),
                indices: Array.from(indices),
                normals: normals ? Array.from(normals) : null,
                editedAt: new Date().toISOString()
              };
            }
          } catch (error) {
            logger.error('Error getting mesh data:', error);
          }
        }
      }
      
      // Check if we have anything to save
      const hasComponentChanges = componentState.deletedComponents.length > 0 || componentState.hiddenComponents.length > 0;
      const hasVertexChanges = editedMeshData !== null;
      
      if (hasComponentChanges || hasVertexChanges) {
        logger.log('Saving changes - components:', hasComponentChanges, 'vertices:', hasVertexChanges);
        
        // Update the scene object
        setSceneObjects(prev => {
          const newSceneObjects = prev.map(obj => {
            if (obj.id === editingObjectId) {
              const updated = { ...obj };
              if (editedMeshData) {
                updated.editedMeshData = editedMeshData;
              }
              // Always save component state (even if empty, to clear previous state)
              updated.deletedComponents = componentState.deletedComponents;
              updated.hiddenComponents = componentState.hiddenComponents;
              return updated;
            }
            return obj;
          });
          
          // Trigger cloud save with the NEW state directly
          setTimeout(async () => {
            try {
              const state = {
                scene_objects: newSceneObjects.map(obj => ({
                  ...obj,
                  modelData: undefined
                })),
                projectors: projectors,
                world_settings: worldSettings,
                project_settings: projectSettings
              };
              await stateApi.save(state);
              logger.log('Edit mode changes saved to cloud');
            } catch (error) {
              logger.error('Cloud save error:', error);
            }
          }, 100);
          
          return newSceneObjects;
        });
        
        toast.success('Changes saved');
      } else {
        toast.info('No changes to save');
      }
      
      setIsDirty(true);
    } else {
      // Restore original mesh if canceling
      if (window.restoreOriginalMesh) {
        window.restoreOriginalMesh();
      }
      toast.info('Changes discarded');
    }
    setEditMode(false);
    setEditingObjectId(null);
    setSelectedVertices([]);
    setVertexGroups({});
    setBoxSelectStart(null);
    setBoxSelectEnd(null);
  }, [editingObjectId, projectors, worldSettings, projectSettings]);

  // Select vertex (with shift for multi-select)
  const selectVertex = useCallback((vertexIndex, addToSelection = false) => {
    setSelectedVertices(prev => {
      if (addToSelection) {
        // Toggle selection
        if (prev.includes(vertexIndex)) {
          return prev.filter(v => v !== vertexIndex);
        }
        return [...prev, vertexIndex];
      }
      // Single select
      return [vertexIndex];
    });
  }, []);

  // Select multiple vertices (for box selection)
  const selectVertices = useCallback((vertexIndices, addToSelection = false) => {
    setSelectedVertices(prev => {
      if (addToSelection) {
        const newSet = new Set([...prev, ...vertexIndices]);
        return Array.from(newSet);
      }
      return vertexIndices;
    });
  }, []);

  // Clear vertex selection
  const clearVertexSelection = useCallback(() => {
    setSelectedVertices([]);
  }, []);

  // Select all vertices
  const selectAllVertices = useCallback((totalVertices) => {
    setSelectedVertices(Array.from({ length: totalVertices }, (_, i) => i));
  }, []);

  // Invert vertex selection
  const invertVertexSelection = useCallback((totalVertices) => {
    setSelectedVertices(prev => {
      const allIndices = Array.from({ length: totalVertices }, (_, i) => i);
      return allIndices.filter(i => !prev.includes(i));
    });
  }, []);

  // Save edit mode state to history (for undo)
  const saveEditModeState = useCallback((vertexData) => {
    setEditModeHistory(prev => [...prev, { vertexData: JSON.parse(JSON.stringify(vertexData)), selectedVertices: [...selectedVertices] }]);
    setEditModeRedoHistory([]);
  }, [selectedVertices]);

  // Undo in edit mode
  const undoEditMode = useCallback(() => {
    if (editModeHistory.length === 0) {
      toast.info('Nothing to undo');
      return null;
    }
    const lastState = editModeHistory[editModeHistory.length - 1];
    setEditModeHistory(prev => prev.slice(0, -1));
    setEditModeRedoHistory(prev => [...prev, lastState]);
    return lastState;
  }, [editModeHistory]);

  // Redo in edit mode
  const redoEditMode = useCallback(() => {
    if (editModeRedoHistory.length === 0) {
      toast.info('Nothing to redo');
      return null;
    }
    const nextState = editModeRedoHistory[editModeRedoHistory.length - 1];
    setEditModeRedoHistory(prev => prev.slice(0, -1));
    setEditModeHistory(prev => [...prev, nextState]);
    return nextState;
  }, [editModeRedoHistory]);

  // Create vertex group
  const createVertexGroup = useCallback((groupName) => {
    if (selectedVertices.length === 0) {
      toast.error('Select vertices first');
      return;
    }
    setVertexGroups(prev => ({
      ...prev,
      [groupName]: [...selectedVertices]
    }));
    toast.success(`Group "${groupName}" created with ${selectedVertices.length} vertices`);
  }, [selectedVertices]);

  // Select vertex group
  const selectVertexGroup = useCallback((groupName) => {
    const group = vertexGroups[groupName];
    if (group) {
      setSelectedVertices(group);
      toast.info(`Selected group "${groupName}"`);
    }
  }, [vertexGroups]);

  // Delete vertex group
  const deleteVertexGroup = useCallback((groupName) => {
    setVertexGroups(prev => {
      const newGroups = { ...prev };
      delete newGroups[groupName];
      return newGroups;
    });
    toast.success(`Group "${groupName}" deleted`);
  }, []);

  // Open Add Projector Dialog (shared between MenuBar and LibraryPanel)
  const openAddProjectorDialog = useCallback((spec) => {
    setAddProjectorSpec(spec);
    setAddWithFrame(true);
    setAddWithClamp(false);
    setAddAsReverseProjector(false); // Reset reverse projector option
    setAddProjectorDialogOpen(true);
  }, []);

  // Handle adding projector with frame/clamp options
  const handleAddProjectorWithOptions = useCallback(() => {
    if (!addProjectorSpec) return;
    
    const specWithOptions = { 
      ...addProjectorSpec, 
      showFrame: addWithFrame,
      showClamp: addWithClamp
    };
    addProjector(specWithOptions, addAsReverseProjector);
    
    let addedMsg = addAsReverseProjector ? 'Added Reverse Projector: ' : 'Added ';
    addedMsg += `${addProjectorSpec.brand} ${addProjectorSpec.model}`;
    if (addWithFrame) addedMsg += ' with frame';
    if (addWithClamp) addedMsg += addWithFrame ? ' and clamp' : ' with clamp';
    
    toast.success(addedMsg);
    setAddProjectorDialogOpen(false);
  }, [addProjectorSpec, addWithFrame, addWithClamp, addAsReverseProjector, addProjector]);

  // Calculate overlap between projectors
  const calculateOverlaps = useCallback(() => {
    if (projectors.length < 2) {
      setOverlapAnalysis(null);
      return null;
    }

    const overlaps = [];
    
    for (let i = 0; i < projectors.length; i++) {
      for (let j = i + 1; j < projectors.length; j++) {
        const p1 = projectors[i];
        const p2 = projectors[j];
        
        if (!p1.power_on || !p2.power_on) continue;
        
        const p1Ratio = ASPECT_RATIOS[p1.aspect_ratio] || ASPECT_RATIOS['16:9'];
        const p2Ratio = ASPECT_RATIOS[p2.aspect_ratio] || ASPECT_RATIOS['16:9'];
        
        const p1Width = p1.throw_distance / p1.throw_ratio;
        const p1Height = p1Width / p1Ratio.value;
        const p2Width = p2.throw_distance / p2.throw_ratio;
        const p2Height = p2Width / p2Ratio.value;
        
        const distance = Math.sqrt(
          Math.pow(p1.position.x - p2.position.x, 2) +
          Math.pow(p1.position.y - p2.position.y, 2) +
          Math.pow(p1.position.z - p2.position.z, 2)
        );
        
        const avgWidth = (p1Width + p2Width) / 2;
        
        if (distance < avgWidth * 1.5) {
          const overlapPercent = Math.max(0, (1 - distance / avgWidth) * 100);
          const overlapPixelsX = Math.round((p1.image_width + p2.image_width) / 2 * overlapPercent / 100);
          const overlapPixelsY = Math.round((p1.image_height + p2.image_height) / 2 * overlapPercent / 100);
          
          overlaps.push({
            projector1: p1.name,
            projector2: p2.name,
            overlapPercent: overlapPercent.toFixed(1),
            overlapPixels: { x: overlapPixelsX, y: overlapPixelsY },
            overlapSize: {
              width: (avgWidth * overlapPercent / 100).toFixed(2),
              height: ((p1Height + p2Height) / 2 * overlapPercent / 100).toFixed(2)
            }
          });
        }
      }
    }
    
    setOverlapAnalysis(overlaps);
    return overlaps;
  }, [projectors]);

  // Load project
  // Default world settings for new projects
  const DEFAULT_WORLD_SETTINGS = {
    backgroundColor: '#09090b',
    showGrid: true,
    gridSize: 500,
    gridColor: '#262626',
    gridLineColor: '#4d4d4d',
    gridStep: 1 // 1 m default
  };

  const loadProject = useCallback((project) => {
    // ─── PROJECT-SWITCH SAFETY (orphan-mesh fix, two-phase swap) ───
    // Setting `sceneObjects`/`projectors` straight to the new arrays
    // batches into a single render — the per-mesh diff cleanup in
    // BabylonScene then has to handle "remove A, B, C AND add X, Y, Z"
    // in one pass, which has known race conditions with async model
    // loaders that resolve later. To make every project switch behave
    // like a clean slate we:
    //   1) flush state to empty in this tick (cleanup pass disposes
    //      every tracked mesh from the previous project);
    //   2) on the next macrotask, push the new project's data, so
    //      creation only sees an empty scene to build into.
    setCurrentProject(project);
    setSceneObjects([]);
    setProjectors([]);
    setMeasurements([]);
    setSavedCameras([]);
    setActiveCameraId(null);
    setSelectedCameraId(null);
    setSelectedObjectId(null);
    setIsDirty(false);
    setUndoHistory([]);
    setRedoHistory([]);

    // Pre-rehydrate while the cleanup pass is still queued — pure work,
    // no React state touched here, safe to compute synchronously.
    const rehydratedSceneObjects = (project.scene_objects || []).map(rehydrateSceneObject);
    const rehydratedProjectors = (project.projectors || []).map(p => rehydrateProjector(p, projectorLibrary));
    const psSource = project.project_settings || {};
    const nextProjectSettings = {
      ...psSource,
      unit: project.unit || psSource.unit || 'm',
      name: project.name || psSource.name
    };
    const nextWorldSettings = project.world_settings ? {
      backgroundColor: project.world_settings.backgroundColor || DEFAULT_WORLD_SETTINGS.backgroundColor,
      showGrid: project.world_settings.showGrid ?? DEFAULT_WORLD_SETTINGS.showGrid,
      gridSize: project.world_settings.gridSize || DEFAULT_WORLD_SETTINGS.gridSize,
      gridColor: project.world_settings.gridColor || DEFAULT_WORLD_SETTINGS.gridColor,
      gridLineColor: project.world_settings.gridLineColor || DEFAULT_WORLD_SETTINGS.gridLineColor,
      gridStep: project.world_settings.gridStep || DEFAULT_WORLD_SETTINGS.gridStep,
    } : null;

    // Defer the second phase one tick so the empty-state cleanup commits first.
    setTimeout(() => {
      setSceneObjects(rehydratedSceneObjects);
      setProjectors(rehydratedProjectors);
      if (nextWorldSettings) {
        setWorldSettings(nextWorldSettings);
        logger.log('Restored world settings from project:', project.world_settings);
      }
      setProjectSettings(nextProjectSettings);
      if (project.measurements?.length > 0) {
        setMeasurements(project.measurements);
      }
      setSavedCameras(project.saved_cameras || []);
    }, 0);
  }, [projectorLibrary]);

  // Clear project
  const clearProject = useCallback(() => {
    saveToHistoryImmediate();
    setCurrentProject(null);
    setSceneObjects([]);
    setProjectors([]);
    setSelectedObjectId(null);
    setIsDirty(false);
    setOverlapAnalysis(null);
    toast.success('Project cleared');
  }, [saveToHistoryImmediate]);

  // New project - resets everything to defaults
  const newProject = useCallback(() => {
    clearProject();
    setProjectSettings({ unit: 'm', name: 'New Project' });
    // Reset world settings to defaults for new projects
    setWorldSettings({
      backgroundColor: '#09090b',
      showGrid: true,
      gridSize: 500,
      gridColor: '#262626',
      gridLineColor: '#4d4d4d',
      gridStep: 1 // 1 m default
    });
    toast.success('New project created');
  }, [clearProject]);

  const value = {
    // Project state
    currentProject,
    setCurrentProject,
    projectSettings,
    setProjectSettings,
    worldSettings,
    setWorldSettings,
    sceneObjects,
    setSceneObjects,
    projectors,
    setProjectors,
    selectedObjectId,
    setSelectedObjectId,
    selectedObject,
    isDirty,
    setIsDirty,
    multiSelectIds,
    setMultiSelectIds,
    
    // Undo/Redo
    undo,
    redo,
    canUndo: undoHistory.length > 0,
    canRedo: redoHistory.length > 0,
    
    // Transform mode
    transformMode,
    setTransformMode,
    gizmoMode,
    setGizmoMode,
    
    // View presets
    viewPreset,
    setViewPreset,
    mainCameraRef,
    
    // Control mode (orbit/pan)
    controlMode,
    setControlMode,
    
    // Snap settings
    snapEnabled,
    setSnapEnabled,
    snapDistance,
    setSnapDistance,
    snapToGroundFnRef,
    
    // Scene panel state
    scenePanelOpen,
    setScenePanelOpen,
    scenePanelMinimized,
    setScenePanelMinimized,
    
    // Vertex Edit Mode
    editMode,
    setEditMode,
    editingObjectId,
    setEditingObjectId,
    selectedVertices,
    setSelectedVertices,
    vertexGroups,
    boxSelectStart,
    setBoxSelectStart,
    boxSelectEnd,
    setBoxSelectEnd,
    enterEditMode,
    exitEditMode,
    selectVertex,
    selectVertices,
    clearVertexSelection,
    selectAllVertices,
    invertVertexSelection,
    saveEditModeState,
    undoEditMode,
    redoEditMode,
    canUndoEditMode: editModeHistory.length > 0,
    canRedoEditMode: editModeRedoHistory.length > 0,
    createVertexGroup,
    selectVertexGroup,
    deleteVertexGroup,
    
    // UI state
    leftPanelOpen,
    setLeftPanelOpen,
    rightPanelOpen,
    setRightPanelOpen,
    activeLeftTab,
    setActiveLeftTab,
    activeRightTab,
    setActiveRightTab,
    
    // Library
    projectorLibrary,
    setProjectorLibrary,
    uploadedFiles,
    setUploadedFiles,
    
    // Overlap analysis
    overlapAnalysis,
    calculateOverlaps,
    
    // Unit conversion
    toCurrentUnit,
    toMeters,
    
    // Actions
    addSceneObject,
    updateSceneObject,
    removeSceneObject,
    addProjector,
    updateProjector,
    addCustomLens,
    removeProjector,
    loadProject,
    clearProject,
    newProject,
    saveToCloud,
    saveProject,
    saveProjectAs,
    isLoading,
    
    // Selection actions
    duplicateSelected,
    duplicateMultiSelected,
    deleteSelected,
    selectAll,
    deselectAll,
    
    // Beam actions
    showAllBeams,
    hideAllBeams,
    
    // Grid toggle
    toggleGrid,
    
    // Undo/Redo history save (for external components like BabylonScene)
    saveToHistoryImmediate,
    
    // Measurements
    measurements,
    setMeasurements,
    measurementMode,
    setMeasurementMode,
    measurementStart,
    addMeasurement,
    updateMeasurement,
    removeMeasurement,
    toggleMeasurementVisibility,
    clearAllMeasurements,
    startMeasurement,
    completeMeasurement,
    cancelMeasurement,
    
    // Add Projector Dialog (shared)
    addProjectorDialogOpen,
    setAddProjectorDialogOpen,
    addProjectorSpec,
    addWithFrame,
    setAddWithFrame,
    addWithClamp,
    setAddWithClamp,
    addAsReverseProjector,
    setAddAsReverseProjector,
    openAddProjectorDialog,
    handleAddProjectorWithOptions,
    
    // Camera System
    savedCameras,
    setSavedCameras,
    activeCameraId,
    setActiveCameraId,
    selectedCameraId,
    setSelectedCameraId,
    createCamera,
    updateCamera,
    deleteCamera,
    duplicateCamera,
    getCameraStateRef,
    captureScreenshotRef,
    
    // Lux Heatmap
    luxHeatmapEnabled,
    setLuxHeatmapEnabled,

    // Display mode (Shaded / Outline)
    displayMode,
    setDisplayMode
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
